// @flow
import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment/dist/moment';

import withGroupClass from '@hoc/withGroupClass';

import AdminLayout from '@layout/components/AdminLayout';

import DescriptionContainer from '_common/components/description-container/DescriptionContainer';
import LabelInformation from '_common/components/label-information/LabelInformation';
import CoachTable from '@coach/components/CoachTable';
import PeriodTable from '@period/components/PeriodTable';

import { formatAddressToString } from '@shared/Locations/services/AddressUtils';
import AuthService from '@user/services/AuthService';
import type { AuthServiceData } from '@user/services/AuthService';
import type { User } from '@user/types';
import RolesHelpers from '@user/helpers/RolesHelpers';
import GroupClassStatusHelpers from '@groupClass/helpers/GroupClassStatusHelpers';

import type { GroupClassDetailed as GroupClassDetailedType } from '@groupClass/types';

import { ADMIN_HEADER_INFORMATION } from '@layout/constants';
import { WEB_PATHS } from '@app/constants/paths';
const { GROUP_CLASS_EDIT } = WEB_PATHS;

type Props = {
  groupClass: GroupClassDetailedType | null,
};

export const GroupClassDetailed = (props: Props): React$Node | null => {
  const { groupClass: initialGroupClass } = props;
  const { t, i18n: { language } } = useTranslation();

  const [ groupClass, setGroupClass ] = useState<GroupClassDetailedType | null>(initialGroupClass);
  const [ currentUser, setCurrentUser ] = useState<User | null>(AuthService.user);

  const handleUserDataUpdate = (data: AuthServiceData): void => {
    setCurrentUser(data.user);
  };

  useEffect(() => AuthService.onChange(handleUserDataUpdate), []);

  const {
    groupClassKey,
    caseNumber,
    seasonKey,
    type,
    address,
    description,
    coaches,
    periods,
    registrationTerms,
    registrationContact,
    startDate,
    endDate,
    exceptDates,
    isOnline,
    difficultyLevel,
    status,
  } = groupClass || {};

  useEffect(() => {
    if ((!groupClass && initialGroupClass) || (groupClass?.id !== initialGroupClass?.id)) {
      setGroupClass(initialGroupClass);
    }
  }, [initialGroupClass, setGroupClass]);

  const { complementNumber } = address || {};

  const addressFormatted = useMemo((): string => (
    address ? formatAddressToString(address) : ''
  ), [address]);

  const startDateFormatted = useMemo((): string => (
    startDate ? moment(startDate).locale(language).format('L') : ''
  ), [startDate, language]);

  const endDateFormatted = useMemo((): string => (
    endDate ? moment(endDate).locale(language).format('L') : ''
  ), [endDate, language]);

  const exceptDateFormatted = useMemo((): string => (
    exceptDates || t('groupClasses.groupClass.detailed.exceptDatesDefault')
  ), [exceptDates, language]);

  const registrationTermsFormatted = useMemo((): string => (
    registrationTerms
  ), [registrationTerms]);

  const groupClassKeyValue = useMemo((): string => (
    groupClassKey || ''
  ), [groupClassKey]);

  const caseNumberValue = useMemo((): string => (
    caseNumber || ''
  ), [caseNumber]);

  const isAdmin = useMemo((): boolean => RolesHelpers.isAdmin(currentUser), [currentUser]);

  const canEditGroupClass = useMemo((): boolean => (
    GroupClassStatusHelpers.canEditGroupClass(status, isAdmin)
  ), [status, isAdmin]);

  if (!groupClass) {
    return null;
  }

  return (
    <AdminLayout activeTab={ ADMIN_HEADER_INFORMATION } groupClass={ groupClass } preventPortraitMode>
      <div className="container-group-class-detailed container">
        <div className="group-class-detailed">
          <div className="group-class-block-title group-class-block-title-detailed">
            <div className="group-class-title">
              <h1 className="group-class-detailed-title">{ t('groupClasses.groupClass.navigation.infos') }</h1>
            </div>
            { canEditGroupClass && (
              <div className="button-container">
                <Link
                  to={ GROUP_CLASS_EDIT.replace(':classId', String(groupClass.id)) }
                  className="button button-primary edit-button"
                >
                  { t('payment.actionEdit') }
                </Link>
              </div>
            ) }
          </div>
          <div className="group-class-container-white">
            <div className="group-class-detailed-show">
              <div className="columns">
                <div className="column is-12">
                  <div className="group-class-detailed-show-general">
                    <div className="columns is-flex-wrap-wrap">
                      <div className="column is-12">
                        <h2 className="group-class-detailed-show-title">{ t('groupClasses.groupClass.detailed.general') }</h2>
                      </div>
                      <div className="column is-6-desktop is-12-tablet p-0">
                        <div className="columns is-flex-wrap-wrap">
                          <LabelInformation
                            title={ t('groupClasses.groupClass.detailed.groupClassKey') }
                            value={ groupClassKeyValue }
                            required
                          />

                          <LabelInformation
                            title={ t('groupClasses.groupClass.detailed.caseNumber') }
                            value={ caseNumberValue }
                            required
                          />

                          <LabelInformation
                            title={ t('groupClasses.groupClass.detailed.address.address') }
                            value={ addressFormatted }
                            required
                          />

                          <LabelInformation
                            title={ t('groupClasses.groupClass.detailed.format') }
                            value={ isOnline ? t('groupClasses.tag.online') : t('groupClasses.tag.inPerson') }
                          />
                        </div>
                      </div>
                      <div className="column is-6-desktop p-0">
                        <div className="columns is-flex-wrap-wrap">
                          <LabelInformation
                            title={ t('groupClasses.groupClass.detailed.seasonKey') }
                            value={ seasonKey }
                            required
                          />

                          <LabelInformation
                            title={ t('groupClasses.groupClass.detailed.classType') }
                            value={ type ? t(`groupClasses.groupClass.type.${ type }`) : '' }
                            classNameValue="class-type"
                            required
                          />

                          <LabelInformation
                            title={ t('groupClasses.groupClass.detailed.status') }
                            value={ status ? t(`groupClasses.groupClass.status.${ status }`) : '' }
                            classNameValue="class-type"
                          />

                          <LabelInformation
                            title={ t('groupClasses.groupClass.detailed.complementNumber') }
                            value={ complementNumber || '' }
                          />

                          <LabelInformation
                            title={ t('groupClasses.groupClass.detailed.difficultyLevel') }
                            value={ !difficultyLevel ? '' : t(`groupClasses.groupClass.difficulty.${ difficultyLevel }`) }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="columns">
                    <div className="column is-12">
                      <DescriptionContainer value={ description } className="show-description" required />
                    </div>
                  </div>
                  <div className="group-class-detailed-show-coach">
                    <div className="columns is-flex-wrap-wrap">
                      <div className="column is-12">
                        <h2 className="group-class-detailed-show-title">{ t('groupClasses.groupClass.detailed.coach') }</h2>
                      </div>
                      <CoachTable coaches={ coaches } />
                    </div>
                  </div>
                  <div className="group-class-detailed-show-dates">
                    <div className="columns is-flex-wrap-wrap">
                      <div className="column is-12">
                        <h2 className="group-class-detailed-show-title">{ t('payment.periodAndPrice') }</h2>
                      </div>
                      <div className="column is-12 p-0 period-table-columns">
                        { periods && periods.length > 0 && (
                          <PeriodTable periods={ periods } />
                        ) }
                      </div>
                      <div className="column is-12 px-0 period-table-columns">
                        <div className="show-dates">
                          <div className="columns is-flex-wrap-wrap">
                            <div className="column is-6-desktop p-0">
                              <div className="columns">
                                <LabelInformation
                                  title={ t('groupClasses.groupClass.detailed.startSeason') }
                                  value={ startDateFormatted }
                                  className="show-start-date"
                                  required
                                />
                              </div>
                            </div>
                            <div className="column is-6-desktop p-0">
                              <div className="columns">
                                <LabelInformation
                                  title={ t('groupClasses.groupClass.detailed.endSeason') }
                                  value={ endDateFormatted }
                                  required
                                />
                              </div>
                            </div>
                          </div>
                          <div className="columns is-flex-wrap-wrap">
                            <div className="column is-6-desktop p-0">
                              <div className="columns">
                                { exceptDates && (
                                  <LabelInformation
                                    title={ t('groupClasses.groupClass.detailed.exceptDate') }
                                    value={ exceptDateFormatted }
                                    className="show-label-full-block"
                                    classNameTitle="show-title-full-block"
                                    classNameValue="show-value-full-block"
                                  />
                                ) }
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="group-class-detailed-show-registration">
                    <div className="columns is-flex-wrap-wrap">
                      <div className="column is-12">
                        <h2 className="group-class-detailed-show-title">{ t('groupClasses.groupClass.detailed.registration') }</h2>
                      </div>
                      <div className="column is-12 p-0">
                        <div className="columns is-flex-wrap-wrap">
                          <div className="column is-6-desktop p-0">
                            <div className="columns">
                              <LabelInformation
                                title={ t('groupClasses.groupClass.detailed.registrationTerm') }
                                value={ registrationTermsFormatted }
                                className="show-label-full-block"
                                classNameTitle="show-title-full-block"
                                classNameValue="show-value-full-block"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="column is-12 p-0">
                        <div className="columns is-flex-wrap-wrap">
                          <div className="column is-6-desktop p-0">
                            <div className="columns">
                              <LabelInformation
                                title={ t('groupClasses.groupClass.detailed.registrationContact') }
                                value={ registrationContact }
                                className="show-label-full-block"
                                classNameTitle="show-title-full-block"
                                classNameValue="show-value-full-block"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default withGroupClass(GroupClassDetailed);
